import React, { FC, useContext, useEffect, useState } from "react";
import { CircularProgress, createStyles, Theme, WithStyles, withStyles, FormControlLabel, Checkbox } from "@material-ui/core";
import { Form } from "react-final-form";
import { TextField } from "../../../lib/components/form/TextField";
import { GreenButton, GreyButton } from "../../../lib/components/ColorButtons";
import { productcharacteristics, ProductCharacteristics, upsertProductCharacteristic } from "../../../lib/api/productcharacteristics";
import { SnackContext } from "../../../lib/context/SnackContext";
import { GenerateErrorMessage } from "../../../lib/helpers/string_methods";
import { SelectField } from "../../../lib/components/form/SelectField";
import { productgroup } from "../../../lib/api/productgroup";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: "100%",
      height: "100%",
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "2rem",
    },
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    },
    actions: {
      display: "flex",
      justifyContent: "flex-end",
      flexDirection: "row",
      gap: "0.25rem",
      alignItems: "center",
    },
    submitButton: {
      display: "flex",
      flexDirection: "row",
    },
    checkboxRow: {
      marginLeft: 15,
      marginRight: "auto",
    },
  });

type LabelingFormProps = {
  id: number;
  handleClose(): void;
} & WithStyles<typeof styles>;

const LabelingFormUnstyled: FC<LabelingFormProps> = ({ id, classes, handleClose }) => {
  const { updateSnack } = useContext(SnackContext);
  const [initData, setInitData] = useState<Partial<ProductCharacteristics>>({});
  const [productGroupsData, setProductGroupsData] = useState([]);
  const [paltrackLabelProcessed, setPaltrackLabelProcessed] = useState(1);
  const [existingCodes, setExistingCodes] = useState<string[]>([]);

  const loadData = async () => {
    try {
      if (id > 0) {
        const result = await productcharacteristics.single(id);
        setInitData(result);
        setPaltrackLabelProcessed(result.paltrack_label_processed ? 1 : 0);
      }
      const productsGroupResult = await productgroup.all();
      console.log("🚀 ~ loadData ~ productsGroupResult:", productsGroupResult);
      setProductGroupsData(productsGroupResult.map((row) => ({ display: row.group_code, value: row.id })));

      const allCharacteristics = await productcharacteristics.all();
      setExistingCodes(allCharacteristics.map((row) => row.characteristic_code.toUpperCase() || ""));
    } catch (error) {
      const err = GenerateErrorMessage(error, "Failed to fetch data. Please try again");
      updateSnack({ show: true, color: "red", message: err });
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  return (
    <div>
      <Form
        initialValues={{ ...initData }}
        validate={(values: ProductCharacteristics) => {
          const errors: { [key: string]: string } = {};
          const code = values.characteristic_code || "";

          if (!/^[A-Za-z0-9]{2}$/.test(code)) {
            errors.characteristic_code = "Length cannot exceed 2 characters.";
          }

          if (id === -1 || id === 0) {
            if (existingCodes.includes(code.toUpperCase())) {
              errors.characteristic_code = "Agent code must be unique";
            }
          }

          if ((values.characteristic_code || "").length > 3) {
            errors.characteristic_code = "Length cannot exceed 3 characters.";
          }

          if ((values.characteristic_description || "").length > 255) {
            errors.characteristic_description = "Length cannot exceed 255 characters.";
          }

          // These are for the old fields - which will be removed again
          if (!/^[A-Za-z0-9]{2}$/.test(values.product_group_code)) {
            errors.product_group_code = "Length cannot exceed 2 characters.";
          }

          if ((values.product_characteristic_code || "").length !== 3) {
            errors.product_characteristic_code = "Length must be exactly 3 characters.";
          }

          if ((values.product_characteristic_description || "").length > 255) {
            errors.product_characteristic_description = "Length cannot exceed 255 characters.";
          }

          return errors;
        }}
        onSubmit={async (values: ProductCharacteristics) => {
          try {
            if (values.characteristic_code) {
              values.characteristic_code = values.characteristic_code.toUpperCase();
            }
            if (values.product_group_code) {
              values.product_group_code = values.product_group_code.toUpperCase();
            }
            if (values.product_characteristic_code) {
              values.product_characteristic_code = values.product_characteristic_code.toUpperCase();
            }

            await upsertProductCharacteristic({
              id: id > 0 ? String(id) : "0",
              data: { ...values, paltrack_label_processed: paltrackLabelProcessed },
            });

            updateSnack({ show: true, color: "green", message: "Successfully submitted!" });
            handleClose();
          } catch (error) {
            const err = GenerateErrorMessage(error, "Failed to submit. Please try again");
            updateSnack({ show: true, color: "red", message: err });
          }
        }}
        render={({ handleSubmit, submitting }: any) => (
          <form onSubmit={handleSubmit} className={classes.form}>
            <div className={classes.container}>
              <SelectField field="labelinggroup_id" title="Target Market" data={productGroupsData} />
              <TextField field="characteristic_code" title="Agent Code" required />
              <TextField field="characteristic_description" title="Agent Description" required />

              {/* This is for the old fields - which will be removed again */}
              <TextField field="product_group_code" title="Product Group Code" required />
              <TextField field="product_characteristic_code" title="Product Characteristic Code" required />
              <TextField field="product_characteristic_description" title="Product Characteristic Description" required />

              <FormControlLabel
                className={classes.checkboxRow}
                label="Paltrack Label Processed:"
                labelPlacement="start"
                control={
                  <Checkbox
                    checked={paltrackLabelProcessed === 1}
                    onChange={() => setPaltrackLabelProcessed(paltrackLabelProcessed === 1 ? 0 : 1)}
                    color="primary"
                    inputProps={{ "aria-label": "paltrack label processed checkbox" }}
                  />
                }
              />
            </div>
            <div className={classes.actions}>
              <GreyButton variant="contained" type="button" disabled={submitting} onClick={handleClose}>
                Cancel
              </GreyButton>
              <GreenButton variant="contained" type="submit" disabled={submitting} className={classes.submitButton}>
                {submitting && <CircularProgress size={24} style={{ marginRight: "0.5rem" }} />}
                <span>Submit</span>
              </GreenButton>
            </div>
          </form>
        )}
      />
    </div>
  );
};

export const LabelingForm = withStyles(styles)(LabelingFormUnstyled);
